import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Phone, Plus, Trash2 } from "lucide-react";
import { getMe, listFields, createField, updateField, deleteField, reorderFields } from "../lib/api";

const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fallback; };

export const FIELD_TYPES = [
  ["text", "Text"], ["number", "Number"], ["date", "Date"], ["dropdown", "Dropdown"],
  ["yes_no", "Yes / No"], ["email", "Email"], ["phone", "Phone"], ["url", "Web address"],
];
const BLANK = { label: "", type: "text", options: "", show_in_table: false, ask_on_calls: false, call_question: "" };

// Admins add the company's own fields for contacts. A field marked "ask on calls" is added to what the AI caller
// finds out during a conversation, and its answer is saved on the contact when the call ends.
export default function FieldsSettings() {
  const [me, setMe] = useState(null);
  const [fields, setFields] = useState([]);
  const [draft, setDraft] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const load = async () => setFields((await listFields()).fields);
  useEffect(() => { Promise.all([getMe().then(setMe), load()]).catch(() => toast.error("Could not load fields")); }, []);
  if (!me) return null;
  const admin = me.role === "admin";

  const add = async () => {
    setBusy(true);
    try {
      await createField({ ...draft, options: draft.type === "dropdown" ? draft.options.split(",") : [] });
      setDraft(BLANK); await load();
      toast.success(`Added "${draft.label}"`);
    } catch (e) { toast.error(errText(e, "Could not add the field")); }
    finally { setBusy(false); }
  };
  const change = async (f, patch) => {
    try { await updateField(f.id, patch); await load(); } catch (e) { toast.error(errText(e, "Could not save")); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Remove "${f.label}"? Values already collected are kept, and come back if you add it again.`)) return;
    try { await deleteField(f.id); await load(); } catch (e) { toast.error(errText(e, "Could not remove it")); }
  };
  const move = async (i, d) => {
    const ids = fields.map(f => f.id); [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    try { setFields((await reorderFields(ids)).fields); } catch (e) { toast.error(errText(e, "Could not reorder")); }
  };

  return <fieldset className="key-group" data-testid="settings-fields">
    <legend>Your own contact fields</legend>
    <p>Add anything you want to track about a contact. Tick <b>Ask on calls</b> and the AI caller finds it out in conversation, then saves the answer on the contact.{!admin && " Only admins can change fields."}</p>
    {fields.map((f, i) => <div className="field-row" key={f.id} data-testid={`field-${f.key}`}>
      <div><b>{f.label}</b><small>{FIELD_TYPES.find(t => t[0] === f.type)?.[1]}{f.type === "dropdown" ? `: ${f.options.join(", ")}` : ""}</small></div>
      <label className="check-inline"><input type="checkbox" checked={f.show_in_table} disabled={!admin} onChange={e => change(f, { show_in_table: e.target.checked })} /> Show in table</label>
      <label className="check-inline"><input type="checkbox" checked={f.ask_on_calls} disabled={!admin} onChange={e => change(f, { ask_on_calls: e.target.checked })} /> <Phone size={11} /> Ask on calls</label>
      {admin && <span className="field-actions">
        <button className="icon-button small" title="Move up" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={13} /></button>
        <button className="icon-button small" title="Move down" disabled={i === fields.length - 1} onClick={() => move(i, 1)}><ArrowDown size={13} /></button>
        <button className="icon-button small" title="Remove" onClick={() => remove(f)}><Trash2 size={13} /></button>
      </span>}
      {f.ask_on_calls && <input className="field-question" defaultValue={f.call_question} disabled={!admin} placeholder="What should the caller find out? e.g. How many vehicles do they run?"
        onBlur={e => e.target.value !== f.call_question && change(f, { call_question: e.target.value })} />}
    </div>)}
    {admin && <>
      <label>Field name<input value={draft.label} onChange={e => setDraft({ ...draft, label: e.target.value })} placeholder="e.g. Fleet size" data-testid="field-label" /></label>
      <label>Type<select value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value })} data-testid="field-type">{FIELD_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
      {draft.type === "dropdown" && <label>Options (separated by commas)<input value={draft.options} onChange={e => setDraft({ ...draft, options: e.target.value })} placeholder="Small, Medium, Large" data-testid="field-options" /></label>}
      <label className="check-inline"><input type="checkbox" checked={draft.show_in_table} onChange={e => setDraft({ ...draft, show_in_table: e.target.checked })} /> Show as a column in Contacts</label>
      <label className="check-inline"><input type="checkbox" checked={draft.ask_on_calls} onChange={e => setDraft({ ...draft, ask_on_calls: e.target.checked })} /> Ask on AI calls</label>
      {draft.ask_on_calls && <label>What should the caller find out?<input value={draft.call_question} onChange={e => setDraft({ ...draft, call_question: e.target.value })} placeholder="How many vehicles do they run?" /></label>}
      <button className="outline-button" onClick={add} disabled={busy || !draft.label.trim()} data-testid="field-add"><Plus size={14} /> Add field</button>
    </>}
  </fieldset>;
}
